import { convertColumnState } from 'ag-grid-community';
import _ from 'lodash';
import moment from 'moment-timezone';
import tinycolor from 'tinycolor2';
import { EMPTY_CHART_REGION } from '@/chart/chart.constants';
import { sqDurationStore, sqWorkbookStore } from '@/core/core.stores';
import { ContentTypes } from '@/dashboard/dashboard.types';
import { momentMeasurementStrings } from '@/datetime/dateTime.utilities';
import { migrateLegacyLayoutPreferences } from '@/main/layoutPreferences';
import { EMPTY_XY_REGION } from '@/scatterPlot/scatterPlot.constants';
import {
  CONDITION_TABLE_TRANSPOSED_HEADER_COLUMN,
  SIMPLE_TABLE_TRANSPOSED_HEADER_COLUMN,
  TableBuilderColumnType,
  TableBuilderHeaderType,
  TableBuilderMode,
  withDefaultFormatting,
} from '@/tableBuilder/tableBuilder.constants';
import { getItemsInScalingTableRow } from '@/tableDefinitionEditor/scalingTable.api';
import { TABLE_DEFINITION_TEMPLATE_OPTIONS } from '@/tableDefinitionEditor/tableDefinition.constants';
import { DEPRECATED_TOOL_NAMES } from '@/toolSelection/investigate.constants';
import {
  COLUMNS_AND_STATS,
  SERIES_PANEL_REQUIRED_TREND_COLUMNS,
  TREND_PANELS_SORT,
} from '@/trendData/trendData.constants';
import { performYAlignment } from '@/utilities/calculations.utilities';
import { sqTimezones } from '@/utilities/datetime.constants';
import { isStatColumn } from '@/utilities/formula.utilities';
import { base64guid } from '@/utilities/guid.utilities';
import {
  isPropertyOrStatColumn,
  isPropertyOrStatOrMetricColumn,
  isStartOrEndColumn,
} from '@/utilities/tableBuilderHelper.utilities';
import { equalsIgnoreCase } from '@/utilities/utilities';
import { VantageNames } from '@/vantage/constants';
import { ASSET_GROUP_VIEW, TABLE_DEFINITION_VIEW, WORKSHEET_VIEW } from '@/worksheet/worksheet.constants';
import { getDefault } from '@/worksheets/worksheetView.utilities';
import { recordStateError, recordStateRead, recordUpgradeCompleted } from '@/worksteps/stateTelemetry.utilities';
import { WORKSTEP_SCHEMA_VERSION } from '@/worksteps/worksteps.constant';
import { sqMetricsApi } from '@seeqdev/api/endpoint/MetricsApi';
import { ProcessTypeEnum } from '@seeqdev/api/model/ThresholdMetricOutputV1';
import { SeeqNames } from '@seeqdev/constants';
import type { ChartRegion } from '@/chart/chart.constants';
import type { TableColumnFilter } from '@/core/tableUtilities/tables.types';
import type { XYPlotRegion } from '@/scatterPlot/scatterPlot.constants';
import type { ColumnOrRow, ColumnOrRowWithDefinitions, TableBuilderSort } from '@/tableBuilder/tableBuilder.types';
import type { TableDefinitionTemplateOptions } from '@/tableDefinitionEditor/tableDefinition.types';
import type { ConditionItem, SeriesItem } from '@/trendData/trendData.types';
import type { Cursor } from '@/utilities/cursor.constants';
import type { AnyProperty } from '@/utilities.types';
import type { ThresholdMetricOutputV1 } from '@seeqdev/api/model/ThresholdMetricOutputV1';

/**
 * A service that manages changes to the schema of workstep state (see configUpgrader for uiConfig upgrades)
 * Since workstep state is just the result of dehydrating all stores this means that any store that makes a
 * backwards incompatible change (e.g. changing a property name, changing a data structure, etc.) must do the following:
 * 1. Create a function named `upgradeX` where X is the current WORKSTEP_SCHEMA_VERSION and add it to the upgraders
 * object.
 * 2. Increase WORKSTEP_SCHEMA_VERSION by one
 */

/**
 * Upgrade the state of a workstep from the specified version to the latest version. Runs the state through a series
 * of transform functions, in order, from the state's version to the specified version.
 *
 * @param state - The workstep state that is result of dehydrating all the stores.
 * @param fromVersion - The version number from when the state was created
 * @param [toVersion=WORKSTEP_SCHEMA_VERSION] - The version number up to which the state will be migrated.
 * @param [fullWorkstep=undefined] - The full workstep object that contains the store state as a field.
 * Useful only for testing.
 * @param [options.recordRead=true] - Whether this is a read of saved state that should count toward the
 * state-read telemetry. False for the echo of a workstep this client just wrote: it is current by
 * construction and counting it would dilute the share of reads that are stale.
 * @returns The transformed state.
 */
export function apply(
  state: any,
  fromVersion: number,
  toVersion?: number,
  fullWorkstep?: any,
  { recordRead = true }: { recordRead?: boolean } = {},
): Promise<any> {
  fromVersion = fromVersion || 1;
  toVersion = toVersion || WORKSTEP_SCHEMA_VERSION;
  const origin = { fromVersion, toVersion };
  if (recordRead) {
    recordStateRead('workstep', fromVersion, toVersion);
  }
  const start = performance.now();
  return _.reduce(
    _.range(fromVersion, toVersion, 1),
    (newState, newVersion) =>
      newState.then(async (state: any) => {
        // Without the await, a rejection from an async upgrader would escape the try uncounted.
        try {
          return await upgraders[`upgrade${newVersion}`](state, fullWorkstep);
        } catch (error) {
          recordStateError({ kind: 'workstep', phase: 'upgrade', upgraderStep: newVersion, error, ...origin });
          throw error;
        }
      }),
    Promise.resolve(state),
  ).then((upgradedState) => {
    if (recordRead) {
      recordUpgradeCompleted('workstep', origin, performance.now() - start);
    }
    return upgradedState;
  });
}

/**
 * Migrates `dataView` to `viewKey` on sqWorksheetStore
 */
function upgrade1(state: any) {
  const dataView = _.get(state, 'stores.sqWorksheetStore.dataView') as string;
  if (_.includes(['trend', 'explore'], dataView)) {
    state.stores.sqWorksheetStore.viewKey = dataView.toUpperCase();
    delete state.stores.sqWorksheetStore.dataView;
  }

  return state;
}

/**
 * Migrates "keywords" to "nameFilter" on sqSearchStore
 */
function upgrade2(state: any) {
  const keywords = _.get(state, 'stores.sqSearchStore.keywords');
  if (!_.isEmpty(keywords)) {
    state.stores.sqSearchStore.nameFilter = keywords;
    delete state.stores.sqSearchStore.keywords;
  }

  return state;
}

/**
 * Upgrades all 'calculation' properties in sqTrendStore.enabledColumns.CAPSULES to 'formula', changing the
 * contents from '@.' to '$series.'
 */
function upgrade3(state: any) {
  const path = ['stores', 'sqTrendStore', 'enabledColumns', 'CAPSULES'];
  const capsuleColumns = _.get(state, path);
  _.forEach(capsuleColumns, function (value, key) {
    if (value.calculation) {
      _.set(state, path.concat(key, 'formula'), _.replace(value.calculation, '@', '$series'));
      _.unset(state, path.concat(key, 'calculation'));
    }
  });

  return state;
}

/**
 * Assigns each and every series to its own lane.
 */
function upgrade4(state: any) {
  const items = _.get(state, 'stores.sqTrendSeriesStore.items') as any;
  const seriesItems = _.reject(items, 'isSeriesFromCapsule');
  const seriesFromCapsuleItems = _.filter(items, 'isSeriesFromCapsule');
  const alignmentOptions = _.times(26, function (idx) {
    return String.fromCharCode(idx + 65);
  });

  _.forEach(seriesItems, function (item: any, idx) {
    if (_.isUndefined(item.lane)) {
      item.lane = idx + 1;
    }

    if (_.isUndefined(item.axisAlign)) {
      item.axisAlign = alignmentOptions[idx];
    }
  });

  _.set(state, 'stores.sqTrendSeriesStore.items', _.concat(seriesItems, seriesFromCapsuleItems));
  return state;
}

/**
 * Migrates to optional 'startTime' column in sqTrendStore
 * Migrates 'anchorId` to `referenceSeries' in sqTrendStore
 * Removes unnecessary properties from enabledColumn definitions
 * Previously, the statistic columns and the property columns would be stored with
 * the entire statistic definition from `TREND_SIGNAL_STATS`. This means that if the
 * statistic column definition changed the data in the store would be stale.
 */
function upgrade5(state: any) {
  // propertyColumns is used as an indicator that changes are needed because it did not exist in v3
  if (!_.isUndefined(_.get(state, 'stores.sqTrendStore.propertyColumns'))) {
    return state;
  }

  // _.set will create the `CAPSULES` object if no columns were ever enabled
  _.set(state, 'stores.sqTrendStore.enabledColumns.CAPSULES.startTime', true);
  const enabledColumns = _.get(state, 'stores.sqTrendStore.enabledColumns.CAPSULES');

  _.forEach(_.filter(enabledColumns, _.isObject), function (column) {
    enabledColumns[column.key] = {
      key: column.key,
      statisticKey: _.join(_.split(column.key, '.', 2), '.'), // statistic.max.1234 -> statistic.max
      referenceSeries: column.anchorId,
    };
  });

  state.stores.sqTrendStore.propertyColumns = {};

  return state;
}

/**
 * Migrates tools that were in RESULTS mode to the new overview tool since RESULTS mode is gone.
 */
function upgrade6(state: any) {
  const displayMode = _.get(state, 'stores.sqInvestigateStore.displayMode');
  if (displayMode === 'RESULTS') {
    state.stores.sqInvestigateStore.displayMode = 'NEW';
    state.stores.sqInvestigateStore.activeTool = 'overview';
  }

  return state;
}

/**
 * Add an empty autoUpdate state.
 */
function upgrade7(state: any) {
  const durationStore = _.get(state, 'stores.sqDurationStore') as any;
  if (!_.isEmpty(durationStore) && _.isUndefined(durationStore.autoUpdate)) {
    state.stores.sqDurationStore.autoUpdate = {};
  }

  return state;
}

/**
 * Expands the display range for ScatterPlot Capsules view to the investigate range.
 */
function upgrade8(state: any) {
  const durationStore = _.get(state, 'stores.sqDurationStore');
  const worksheetView = _.get(state, 'stores.sqWorksheetStore.viewKey');
  const scatterPlotMode = _.get(state, 'stores.sqScatterPlotStore.plotMode');
  if (
    !_.isEmpty(durationStore) &&
    worksheetView === 'SCATTER_PLOT' &&
    !_.isEmpty(scatterPlotMode) &&
    scatterPlotMode === 'CAPSULES'
  ) {
    state.stores.sqDurationStore.displayRange.start = _.get(state, 'stores.sqDurationStore.investigateRange.start');
    state.stores.sqDurationStore.displayRange.end = _.get(state, 'stores.sqDurationStore.investigateRange.end');
  }

  return state;
}

/**
 * Migrates custom histogram bin colors to new store name.
 */
function upgrade9(state: any) {
  const colorConfig = _.get(state, 'stores.sqTrendTableStore.colorConfig');
  if (_.isEmpty(colorConfig)) {
    return state;
  }

  state.stores.sqTrendTableStore.binConfig = _.mapValues(colorConfig, function (value) {
    return { color: value };
  });

  _.unset(state, 'stores.sqTrendTableStore.colorConfig');
  return state;
}

/**
 * Migrates from selectedIds to selectedCapsules. Renames editingCapsuleSetId to editingId for consistency
 */
function upgrade10(state: any) {
  const capsuleStore = _.get(state, 'stores.sqTrendCapsuleStore');

  if (!_.isEmpty(capsuleStore)) {
    if (state.stores.sqTrendCapsuleStore.editingCapsuleSetId) {
      state.stores.sqTrendCapsuleStore.editingId = state.stores.sqTrendCapsuleStore.editingCapsuleSetId;
      delete state.stores.sqTrendCapsuleStore.editingCapsuleSetId;
    }

    delete state.stores.sqTrendCapsuleStore.selectedIds;
  }

  return state;
}

/**
 * Migrates the search store mode
 */
function upgrade11(state: any) {
  _.forEach(['sqSearchStore', 'sqModalSearchStore'], (store) => {
    if (_.get(state, `stores.${store}.isAssetBrowsing`) && _.get(state, `stores.${store}.currentAsset`)) {
      _.set(state, `stores.${store}.mode`, 'tree');
    } else if (_.get(state, `stores.${store}.nameFilter`)) {
      _.set(state, `stores.${store}.mode`, 'search');
    }

    _.unset(state, `stores.${store}.isAssetBrowsing`);
  });

  return state;
}

/**
 * Updates Duration.store to rename 'offset' to 'displayRangeEndOffset' and updates the MODE if the auto update isn't
 * enabled.
 */
function upgrade12(state: any) {
  if (!_.isEmpty(state.stores.sqDurationStore)) {
    if (!_.isNil(state.stores.sqDurationStore.autoUpdate.enabled)) {
      if (state.stores.sqDurationStore.autoUpdate.enabled === false) {
        state.stores.sqDurationStore.autoUpdate.mode = 'OFF';
      }
      delete state.stores.sqDurationStore.autoUpdate.enabled;
    }
  }
  return state;
}

/**
 * Updates the BaseSignalStore to rename 'samples' to 'sampleDisplayOption'.
 */
function upgrade13(state: any) {
  const items = _.get(state, 'stores.sqTrendSeriesStore.items') as any;
  const seriesItems = _.reject(items, 'isSeriesFromCapsule');
  const seriesFromCapsuleItems = _.filter(items, 'isSeriesFromCapsule');
  const updatedSeriesItems: any[] = [];
  _.forEach(seriesItems, function (item: any) {
    if (_.has(item, 'samples')) {
      item.sampleDisplayOption = item.samples;
      item = _.omit(item, 'samples');
    }
    updatedSeriesItems.push(item);
  });

  _.set(state, 'stores.sqTrendSeriesStore.items', _.concat(updatedSeriesItems, seriesFromCapsuleItems));

  return state;
}

/**
 * Previously, when the manual condition tool was opened the capsules were stored in the manual condition store. This
 * upgrade step moves them to the sqCapsuleGroup store that previously didn't exist.
 */
function upgrade14(state: any) {
  if (!_.isEmpty(state.stores.sqManualConditionStore)) {
    if (!_.isNil(state.stores.sqManualConditionStore.capsules)) {
      _.set(state.stores, ['sqCapsuleGroupStore', 'capsules'], state.stores.sqManualConditionStore.capsules);
      delete state.stores.sqManualConditionStore.capsules;
    }
  }
  return state;
}

/**
 * Removes the `displayFavorites` property (which has been replaced in favor of `displaySelector` This will
 * effectively reset to the "All" view
 */
function upgrade15(state: any) {
  if (!_.isEmpty(state.stores.sqWorkbenchStore)) {
    if (!_.isNil(_.get(state, 'stores.sqWorkbenchStore.displayFavorites'))) {
      delete state.stores.sqWorkbenchStore.displayFavorites;
    }
  }
  return state;
}

/**
 * Updates the sqReportStore duration object to a number
 */
function upgrade16(state: any) {
  if (!_.isEmpty(state.stores.sqReportStore)) {
    const path = ['stores', 'sqReportStore', 'dateVariables'];
    const dateVariables = _.get(state, path);

    _.forEach(dateVariables, function (dateVariable) {
      if (!_.isNil(dateVariable.auto)) {
        if (!_.isNil(dateVariable.auto.duration)) {
          if (!_.isNil(dateVariable.auto.duration.value)) {
            const newValue = moment.duration(
              dateVariable.auto.duration.value,
              momentMeasurementStrings(dateVariable.auto.duration.units),
            );

            dateVariable.auto.duration = newValue.valueOf();
          }
        }
      }
    });

    _.set(state, 'stores.sqReportStore.dateVariables', dateVariables);
  }

  return state;
}

/**
 * Updates sqPeriodicConditionStore timezone property from offset to name
 */
function upgrade17(state: any) {
  const offset = _.get(state.stores, 'sqPeriodicConditionStore.timezone');
  if (offset) {
    state.stores.sqPeriodicConditionStore.timezone = _.get(sqTimezones.findMostCommonTimeZone(offset), 'name');
  }

  return state;
}

/**
 * Removes ancillaries from signal stores, the ancillaries used to be persisted. This migrates them
 * to a displayedAncillaryItemIds property. In the future the ancillaries will be loaded dynamically
 * if they exist in this array.
 */
function upgrade18(state: any) {
  const displayedAncillaryItemIdsMapping: Record<string, string[]> = {};
  const sqTrendSeriesStore = _.get(state.stores, 'sqTrendSeriesStore');
  const sqTrendScalarStore = _.get(state.stores, 'sqTrendScalarStore');
  _.forEach([sqTrendSeriesStore, sqTrendScalarStore], (sqSignalStore) => {
    if (!sqSignalStore || !sqSignalStore.items) {
      return;
    }

    const [ancillaries, items] = _.partition<any>(sqSignalStore.items, 'isChildOf');
    sqSignalStore.items = items;
    _.forEach(ancillaries, (item) => {
      const displayedAncillaryItemIds: string[] = _.get(displayedAncillaryItemIdsMapping, item.isChildOf, []);
      displayedAncillaryItemIds.push(item.id);
      _.set(displayedAncillaryItemIdsMapping, item.isChildOf, displayedAncillaryItemIds);
    });
  });

  // Note: scalars can be ancillaries, but cannot have ancillaries
  _.forEach(_.get(sqTrendSeriesStore, 'items'), (item) => {
    const displayedAncillaryItemIds = displayedAncillaryItemIdsMapping[item.id];
    if (displayedAncillaryItemIds) {
      item.displayedAncillaryItemIds = displayedAncillaryItemIds;
    }
  });

  return state;
}

/**
 * Updates topic date ranges to have the "auto" property which came with R20 and live docs.
 */
function upgrade19(state: any) {
  const dateVariables = _.get<any[]>(state.stores, 'sqReportStore.dateVariables', []);
  _.forEach(dateVariables, (variable) => {
    if (!_.has(variable, 'auto')) {
      variable.auto = { enabled: false };
    }
  });

  if (!_.isEmpty(dateVariables)) {
    state.stores.sqReportStore.dateVariables = dateVariables;
  }

  return state;
}

/**
 * Removes duplicate items from the details pane after a backend upgrade that replaces duplicates with the keeper
 * in worksteps. See CRAB-13226.
 */
function upgrade20(state: any) {
  if (_.has(state.stores, 'sqTrendSeriesStore.items')) {
    state.stores.sqTrendSeriesStore.items = _.uniqBy(state.stores.sqTrendSeriesStore.items, 'id');
  }

  if (_.has(state.stores, 'sqTrendScalarStore.items')) {
    state.stores.sqTrendScalarStore.items = _.uniqBy(state.stores.sqTrendScalarStore.items, 'id');
  }

  if (_.has(state.stores, 'sqTrendCapsuleSetStore.items')) {
    state.stores.sqTrendCapsuleSetStore.items = _.uniqBy(state.stores.sqTrendCapsuleSetStore.items, 'id');
  }

  return state;
}

/**
 * Removes series from capsules that were stored in the trend series store. Before Seeq R21.0.40.00 series from
 * capsule segments needed to be preserved in the workstep because they represented whether one of the capsule
 * time "eyeballs" was selected. Since the "eyeball" mechanism was removed (CRAB-11107), we don't need to the
 * segments to maintain that state.
 */
function upgrade21(state: any) {
  if (_.has(state.stores, 'sqTrendSeriesStore.items')) {
    state.stores.sqTrendSeriesStore.items = _.reject(state.stores.sqTrendSeriesStore.items, 'isChildOf');
  }

  return state;
}

/**
 * Renames sqTrendStore.hideDimmedCapsules to sqTrendStore.hideUnselectedItems
 */
function upgrade22(state: any) {
  if (_.has(state.stores, 'sqTrendStore.hideDimmedCapsules')) {
    state.stores.sqTrendStore.hideUnselectedItems = state.stores.sqTrendStore.hideDimmedCapsules;
    delete state.stores.sqTrendStore.hideDimmedCapsules;
  }

  return state;
}

/**
 * Adds an id to scorecard columns.
 */
function upgrade23(state: any) {
  if (_.has(state.stores, 'sqTrendMetricStore.scorecardColumns')) {
    state.stores.sqTrendMetricStore.scorecardColumns = _.map(
      state.stores.sqTrendMetricStore.scorecardColumns,
      (column) => ({ id: base64guid(), ...column }),
    );
  }

  return state;
}

/**
 * Upgrades empty view-region to the new version of it
 */
function upgrade24(state: any) {
  const oldXyRegion = _.pick(EMPTY_CHART_REGION, ['xMin', 'xMax', 'yMin', 'yMax']);
  _.forEach(['selectedRegion', 'viewRegion'], (region) => {
    if (_.isEqual(_.get(state.stores, `sqScatterPlotStore.${region}`), oldXyRegion)) {
      state.stores.sqScatterPlotStore[region] = EMPTY_CHART_REGION;
    }
  });

  return state;
}

/**
 * Migrates item detail panes that used the standardDeviation stat to the stdDev stat.
 */
function upgrade25(state: any) {
  const stdDev = _.get(state.stores, "sqTrendStore.enabledColumns.SERIES['statistics.standardDeviation']");
  if (stdDev) {
    delete state.stores.sqTrendStore.enabledColumns.SERIES['statistics.standardDeviation'];
    state.stores.sqTrendStore.enabledColumns.SERIES['statistics.stdDev'] = true;
  }
  return state;
}

/**
 * Changes SCORECARD view to TABLE and moves scorecard properties to table builder. If in TABLE view and items are
 * selected it unselects all items so that user will see all the metrics, not just the selected ones (to preserve
 * previous behavior where selection did not influence the table). Moves the scorecardOrder to lane now that order
 * is dictated by the details pane.
 */
function upgrade32(state: any) {
  const viewKey = _.get(state.stores, 'sqWorksheetStore.viewKey');
  if (viewKey === 'SCORECARD') {
    state.stores.sqWorksheetStore.viewKey = WORKSHEET_VIEW.TABLE;
    let maxLane = 0;
    const selectedIds: string[] = [];
    _.forEach(
      [
        'sqTrendSeriesStore',
        'sqTrendCapsuleSetStore',
        'sqTrendMetricStore',
        'sqTrendScalarStore',
        'sqTrendTableStore',
        'sqTrendCapsuleStore',
      ],
      (storeName) => {
        _.forEach(_.get(state.stores[storeName], 'items'), (item, index) => {
          if (storeName !== 'sqTrendMetricStore' && item.lane > maxLane) {
            maxLane = item.lane;
          }

          if (item.selected) {
            selectedIds.push(item.id);
            state.stores[storeName].items[index].selected = false;
          }
        });
      },
    );
    if (selectedIds.length) {
      state.stores.sqWorksheetStore.selectedIdsForView = {
        [getDefault().selectedItemsRealm]: selectedIds,
      };
    }

    _.forEach(state.stores.sqTrendMetricStore?.items, (item, index) => {
      state.stores.sqTrendMetricStore.items[index].lane = item.scorecardOrder + maxLane + 1;
    });

    if (!_.isUndefined(state.stores.sqTrendStore?.panelSorters)) {
      state.stores.sqTrendStore.panelSorters.SERIES = TREND_PANELS_SORT.SERIES;
    }
  }

  if (_.isUndefined(state.stores.sqTableBuilderStore)) {
    state.stores.sqTableBuilderStore = {};
  }

  if (_.has(state.stores, 'sqTrendMetricStore.scorecardColumns')) {
    state.stores.sqTableBuilderStore.columns = state.stores.sqTrendMetricStore.scorecardColumns;
    delete state.stores.sqTrendMetricStore.scorecardColumns;
  }

  if (_.has(state.stores, 'sqTrendMetricStore.scorecardHeaders')) {
    state.stores.sqTableBuilderStore.headers = state.stores.sqTrendMetricStore.scorecardHeaders;
    delete state.stores.sqTrendMetricStore.scorecardHeaders;
  }

  return state;
}

/**
 * Sets the activeTool to "overview" for deprecated tools
 */
function upgrade33(state: any) {
  if (_.includes(DEPRECATED_TOOL_NAMES, state?.stores?.sqInvestigateStore?.activeTool)) {
    state.stores.sqInvestigateStore.activeTool = 'overview';
  }

  return state;
}

/**
 * Upgrades the table builder store state to the new structure which supports both simple and condition tables
 */
function upgrade34(state: any) {
  if (!_.isUndefined(state.stores.sqTableBuilderStore) && !_.isEmpty(state.stores.sqTableBuilderStore)) {
    state.stores.sqTableBuilderStore.mode = TableBuilderMode.Condition;
    if (
      _.has(state.stores, 'sqTableBuilderStore.columns') &&
      !_.has(state.stores, `sqTableBuilderStore.columns.${TableBuilderMode.Condition}`)
    ) {
      state.stores.sqTableBuilderStore.columns = {
        [TableBuilderMode.Condition]: state.stores.sqTableBuilderStore.columns,
        [TableBuilderMode.Simple]: _.map(SERIES_PANEL_REQUIRED_TREND_COLUMNS, (key) => ({ key })),
      };
    }
    if (
      _.has(state.stores, 'sqTableBuilderStore.isTransposed') &&
      !_.has(state.stores, `sqTableBuilderStore.isTransposed.${TableBuilderMode.Condition}`)
    ) {
      state.stores.sqTableBuilderStore.isTransposed = {
        [TableBuilderMode.Condition]: state.stores.sqTableBuilderStore.isTransposed,
        [TableBuilderMode.Simple]: false,
      };
    }
  }
  return state;
}

/**
 * Upgrades the table builder store state to include the new variations in header options
 */
function upgrade35(state: any) {
  if (!_.isUndefined(state.stores.sqTableBuilderStore) && !_.isEmpty(state.stores.sqTableBuilderStore)) {
    // skip if the table already have the new header format.
    // This upgrade was not executed until WORKSTEP_SCHEMA_VERSION = 37
    if (
      _.has(state.stores, 'sqTableBuilderStore.headers') &&
      _.isNil(state.stores.sqTableBuilderStore.headers[TableBuilderMode.Simple])
    ) {
      state.stores.sqTableBuilderStore.headers = {
        [TableBuilderMode.Condition]: state.stores.sqTableBuilderStore.headers,
        [TableBuilderMode.Simple]: {
          type: TableBuilderHeaderType.StartEnd,
          format: 'lll',
        },
      };
    }
  }
  return state;
}

/**
 * Switches from condition to simple mode if the table builder store contains simple metrics and migrate the
 * content which can be displayed in the simple table. If the old table contains only simple metrics the user
 * should see no change after migration.
 * The switch to simple mode is done if there is at least one simple metric.
 */
function upgrade36(state: any) {
  const worksheetView = _.get(state, 'stores.sqWorksheetStore.viewKey');
  const tableStore = _.get(state, 'stores.sqTableBuilderStore');

  // Convert capsule property Value header to Value (Original)
  const capsuleProperty =
    tableStore && _.get(state.stores, `sqTableBuilderStore.headers[${TableBuilderMode.Condition}].property`);
  if (equalsIgnoreCase(capsuleProperty, SeeqNames.CapsuleProperties.Value)) {
    state.stores.sqTableBuilderStore.headers[TableBuilderMode.Condition].property =
      `${SeeqNames.CapsuleProperties.Value} (Original)`;
  }

  if (tableStore && tableStore.mode === TableBuilderMode.Condition) {
    const trendMetricItems = _.get(state, 'stores.sqTrendMetricStore.items');
    const allMetricPromises = _.map(trendMetricItems, (metric) => sqMetricsApi.getMetric({ id: metric.id }));

    return Promise.all(allMetricPromises).then((allMetrics: any) => {
      const [simpleMetrics, nonSimpleMetrics] = _.partition(
        allMetrics,
        (metric) => metric.data?.processType === ProcessTypeEnum.Simple,
      );
      const selectedSimpleMetrics = _.filter(
        trendMetricItems,
        (trendMetric) =>
          _.some(simpleMetrics, (metric) => metric.data.id === trendMetric.id) && trendMetric.selected === true,
      );
      const selectedNonSimpleMetrics = _.filter(
        trendMetricItems,
        (trendMetric) =>
          _.some(nonSimpleMetrics, (metric) => metric.data.id === trendMetric.id) && trendMetric.selected === true,
      );

      if (_.isEmpty(selectedSimpleMetrics) && !_.isEmpty(selectedNonSimpleMetrics)) {
        // nothing to change. Keep the existing table in condition mode.
      } else if (!_.isEmpty(simpleMetrics)) {
        tableStore.mode = TableBuilderMode.Simple;
        tableStore.headers[TableBuilderMode.Simple] = tableStore.headers[TableBuilderMode.Condition];

        // if isTransposed was not initialized until now, add the field in the store.
        if (_.has(state.stores, 'sqTableBuilderStore.isTransposed')) {
          tableStore.isTransposed[TableBuilderMode.Simple] = tableStore.isTransposed[TableBuilderMode.Condition];
        } else {
          tableStore.isTransposed = {
            [TableBuilderMode.Condition]: false,
            [TableBuilderMode.Simple]: false,
          };
        }

        let nameColumnFound = false;
        // transfer only the first 'name' column and all free text columns to simple mode
        tableStore.columns[TableBuilderMode.Simple] = _.chain(tableStore.columns[TableBuilderMode.Condition])
          .filter((column) => {
            if (column.type === COLUMNS_AND_STATS.name.key) {
              if (!nameColumnFound) {
                nameColumnFound = true;
                return true;
              }
              return false;
            } else {
              return true;
            }
          })
          .map((column) => {
            switch (column.type) {
              case COLUMNS_AND_STATS.name.key:
                return {
                  ..._.pick(column, ['backgroundColor', 'header']),
                  key: COLUMNS_AND_STATS.name.key,
                };
              case TableBuilderColumnType.Text:
                return {
                  ..._.pick(column, ['backgroundColor', 'header', 'cells', 'type']),
                  key: column.id,
                };
              default:
                return column;
            }
          })
          .value();

        // if we have also condition metrics (mixed table), keep existing columns
        if (_.isEmpty(nonSimpleMetrics)) {
          tableStore.columns[TableBuilderMode.Condition] = [];
        }

        // add metric value column as the last column and set headerOverridden flag
        tableStore.columns[TableBuilderMode.Simple].push({
          key: 'metricValue',
          headerOverridden: true,
        });

        // Select all metrics if none is selected and we have other items in the details pane.
        // When upgrading from condition to simple mode we want to see only metrics in the table by default and no
        // other item.
        const noMetricSelected = !_.some(trendMetricItems, { selected: true });
        const otherItemsPresent =
          _.get(state, 'stores.sqTrendCapsuleSetStore.items.length') ||
          _.get(state, 'stores.sqTrendScalarStore.items.length') ||
          _.get(state, 'stores.sqTrendSeriesStore.items.length');

        const allMetrics = _.get(state, 'stores.sqTrendMetricStore.items');
        const allSelectedMetricIds = _.map(_.filter(allMetrics, 'selected'), 'id');

        if (otherItemsPresent) {
          // The old table displayed only metrics.
          if (worksheetView === 'TABLE') {
            // Keep the selections for trend view, but unselect all non metric in the table view
            const allConditions = _.get(state, 'stores.sqTrendCapsuleSetStore.items');
            const allScalars = _.get(state, 'stores.sqTrendScalarStore.items');
            const allSeries = _.get(state, 'stores.sqTrendSeriesStore.items');

            const allSelectedConditionIds = _.map(_.filter(allConditions, 'selected'), 'id');
            const allSelectedScalarIds = _.map(_.filter(allScalars, 'selected'), 'id');
            const allSelectedSeriesIds = _.map(_.filter(allSeries, 'selected'), 'id');

            const allSelectedIds = _.concat(
              allSelectedConditionIds,
              allSelectedScalarIds,
              allSelectedSeriesIds,
              allSelectedMetricIds,
            );
            state.stores.sqWorksheetStore.selectedIdsForView = {
              TREND: allSelectedIds,
            };

            _.forEach(allConditions, (item) => {
              item.selected = false;
            });
            _.forEach(allScalars, (item) => {
              item.selected = false;
            });
            _.forEach(allSeries, (item) => {
              item.selected = false;
            });

            if (noMetricSelected) {
              // select all metrics.
              _.forEach(trendMetricItems, (item) => {
                item.selected = true;
              });
            }
          } else {
            // If no metric selected, select all metrics for table view and do not touch the selections of the current
            // view. Else set selected metrics as selected in the table view
            if (noMetricSelected) {
              state.stores.sqWorksheetStore.selectedIdsForView = {
                TABLE: _.map(trendMetricItems, 'id'),
              };
            } else {
              state.stores.sqWorksheetStore.selectedIdsForView = {
                TABLE: allSelectedMetricIds,
              };
            }
          }
        }
      }
      return state;
    });
  }
  return state;
}

/**
 * Adds the default style to all headers in simple and condition mode. and converts columns to the new format.
 */
function upgrade37(state: any) {
  const tableStore = _.get(state, 'stores.sqTableBuilderStore');
  if (!_.isUndefined(tableStore) && !_.isEmpty(tableStore)) {
    if (_.has(tableStore, ['columns', TableBuilderMode.Simple])) {
      _.forEach(tableStore.columns[TableBuilderMode.Simple], (column, index) => {
        state.stores.sqTableBuilderStore.columns[TableBuilderMode.Simple][index] = column.isCustomProperty
          ? {
              ..._.omit(column, ['isCustomProperty']),
              type: TableBuilderColumnType.Property,
              headerTextAlign: 'center',
              headerTextStyle: ['bold'],
              textColor: column.backgroundColor && tinycolor(column.backgroundColor).isDark() ? '#fff' : undefined,
            }
          : {
              ...column,
              headerTextAlign: 'center',
              headerTextStyle: ['bold'],
              textColor: column.backgroundColor && tinycolor(column.backgroundColor).isDark() ? '#fff' : undefined,
            };
      });
    }

    if (_.has(tableStore, ['columns', TableBuilderMode.Condition])) {
      _.forEach(state.stores.sqTableBuilderStore.columns[TableBuilderMode.Condition], (column, index) => {
        state.stores.sqTableBuilderStore.columns[TableBuilderMode.Condition][index] =
          column.type === TableBuilderColumnType.Text
            ? {
                ..._.omit(column, ['id', 'shortTitle']),
                key: column.id || column.key,
                headerTextAlign: 'center',
                headerTextStyle: ['bold'],
                textColor: column.backgroundColor && tinycolor(column.backgroundColor).isDark() ? '#fff' : undefined,
              }
            : {
                ..._.omit(column, ['id', 'shortTitle', 'type']),
                key: column.type || column.key,
                headerTextAlign: 'center',
                headerTextStyle: ['bold'],
                textColor: column.backgroundColor && tinycolor(column.backgroundColor).isDark() ? '#fff' : undefined,
              };
      });
    }
  }

  return state;
}

/**
 * Accommodates the new ability for table builder to include conditions. Selects only metrics if user is in
 * condition table builder to ensure it looks the same.
 */
function upgrade38(state: any) {
  const tableStoreMode = _.get(state, 'stores.sqTableBuilderStore.mode');
  const viewKey = _.get(state.stores, 'sqWorksheetStore.viewKey');
  if (viewKey === WORKSHEET_VIEW.TABLE && tableStoreMode === TableBuilderMode.Condition) {
    const trendMetricItems = _.get(state, 'stores.sqTrendMetricStore.items');
    const metricSelected = _.some(trendMetricItems, 'selected');
    const conditionItems = _.get(state, 'stores.sqTrendCapsuleSetStore.items');
    if (!metricSelected && !_.isEmpty(conditionItems)) {
      _.forEach(trendMetricItems, (item, index) => {
        state.stores.sqTrendMetricStore.items[index].selected = true;
      });
    } else if (metricSelected && _.some(conditionItems, 'selected')) {
      _.forEach(conditionItems, (item, index) => {
        state.stores.sqTrendCapsuleSetStore.items[index].selected = false;
      });
    }
  }

  return state;
}

/**
 * Adds the new sort and filter criteria to existing table builder worksteps.
 */
function upgrade39(state: any) {
  if (!_.isUndefined(state.stores.sqTableBuilderStore) && !_.isEmpty(state.stores.sqTableBuilderStore)) {
    if (_.isNil(state.stores.sqTableBuilderStore.itemFilters)) {
      state.stores.sqTableBuilderStore.itemFilters = {};
    }
    if (_.isNil(state.stores.sqTableBuilderStore.itemSorts)) {
      state.stores.sqTableBuilderStore.itemSorts = {};
    }
  }
  return state;
}

/**
 * Removes condition and signal entries from the conditionToSeriesGrouping if those conditions are not present
 * in the details pane. Previously there were cases where conditions and signals that were removed from the details
 * pane were not also removed from the conditionToSeriesGrouping. See CRAB-25536.
 */
function upgrade42(state: any) {
  const conditionToSeriesGrouping = _.get(state, 'stores.sqWorksheetStore.conditionToSeriesGrouping');

  if (!_.isEmpty(conditionToSeriesGrouping)) {
    const detailsPaneConditionIds = _.chain(state).get('stores.sqTrendCapsuleSetStore.items').map('id').value();

    const detailsPaneSignalIds = _.chain(state).get('stores.sqTrendSeriesStore.items').map('id').value();

    const cleanedConditionToSeriesGrouping = _.chain(conditionToSeriesGrouping)
      .pickBy((signals, conditionId) => _.includes(detailsPaneConditionIds, conditionId))
      .mapValues((signals) => _.intersection(signals, detailsPaneSignalIds))
      .value();

    _.set(state, 'stores.sqWorksheetStore.conditionToSeriesGrouping', cleanedConditionToSeriesGrouping);
  }

  return state;
}

function upgrade43(state: any) {
  if (_.has(state.stores, 'sqTrendCapsuleStore')) {
    state.stores.sqTrendCapsuleStore.capsulesPerPage = 30;
  }

  return state;
}

/**
 * Sets conditionTable.isTransposed to false on existing worksteps to prevent changing existing tables
 */
function upgrade44(state: any) {
  if (_.isUndefined(state.stores.sqTableBuilderStore?.isTransposed?.[TableBuilderMode.Condition])) {
    _.set(state, `stores.sqTableBuilderStore.isTransposed.${[TableBuilderMode.Condition]}`, false);
  }
  return state;
}

/**
 * Changes xSeries and ySeries in the scatter plot store to xSignal and ySignals
 */
function upgrade45(state: any) {
  if (!_.isEmpty(state.stores?.sqScatterPlotStore?.xSeries)) {
    state.stores.sqScatterPlotStore.xSignal = state.stores.sqScatterPlotStore.xSeries;
  }
  delete state.stores?.sqScatterPlotStore?.xSeries;

  if (!_.isEmpty(state.stores?.sqScatterPlotStore?.ySeries)) {
    state.stores.sqScatterPlotStore.ySignals = [state.stores.sqScatterPlotStore.ySeries];
  }
  delete state.stores?.sqScatterPlotStore?.ySeries;

  return state;
}

/**
 * Changes the format of viewRegion and selectedRegion in the scatter plot store to support multiple y signals
 */
function upgrade46(state: any) {
  const oldRegionToNewRegion: (region: ChartRegion) => XYPlotRegion = (region = EMPTY_CHART_REGION) => {
    const yId = state.stores.sqScatterPlotStore.ySignals[0].id;
    const newRegion = {
      x: {
        min: region.xMin,
        max: region.xMax,
      },
      ys: {} as Record<string, { min: number; max: number }>,
    };

    if (!_.isNil(region.yMin) && !_.isNil(region.yMax) && !_.isNil(yId)) {
      newRegion.ys[yId] = {
        min: region.yMin,
        max: region.yMax,
      };
    }

    return newRegion;
  };

  if (
    (state.stores.sqScatterPlotStore?.ySignals?.length ?? 0 > 0) &&
    state.stores.sqScatterPlotStore?.xSignal !== null
  ) {
    if (
      !_.isEqual(state.stores.sqScatterPlotStore?.selectedRegion, EMPTY_CHART_REGION) &&
      !state.stores.sqScatterPlotStore?.selectedRegion?.x
    ) {
      _.set(
        state,
        'stores.sqScatterPlotStore.selectedRegion',
        oldRegionToNewRegion(state.stores.sqScatterPlotStore?.selectedRegion),
      );
    } else if (_.isEqual(state.stores.sqScatterPlotStore?.selectedRegion, EMPTY_CHART_REGION)) {
      _.set(state, 'stores.sqScatterPlotStore.selectedRegion', EMPTY_XY_REGION);
    }

    if (
      !_.isEqual(state.stores.sqScatterPlotStore?.viewRegion, EMPTY_CHART_REGION) &&
      !state.stores.sqScatterPlotStore?.viewRegion?.x
    ) {
      _.set(
        state,
        'stores.sqScatterPlotStore.viewRegion',
        oldRegionToNewRegion(state.stores.sqScatterPlotStore?.viewRegion),
      );
    } else if (_.isEqual(state.stores.sqScatterPlotStore?.viewRegion, EMPTY_CHART_REGION)) {
      _.set(state, 'stores.sqScatterPlotStore.viewRegion', EMPTY_XY_REGION);
    }
  } else {
    _.set(state, 'stores.sqScatterPlotStore.selectedRegion', EMPTY_XY_REGION);
    _.set(state, 'stores.sqScatterPlotStore.viewRegion', EMPTY_XY_REGION);
  }

  return state;
}

function upgrade47(state: any) {
  if (!_.isEmpty(state?.stores?.sqTrendStore?.capsuleAlignment)) {
    // Set all scalars to be visible
    const scalars = state.stores?.sqTrendScalarStore?.items ?? [];
    _.set(
      state,
      'stores.sqTrendScalarStore.items',
      _.map(scalars, (scalar) => ({
        ...scalar,
        visible: true,
      })),
    );

    // Align the series respective to those that share their lane
    const capsuleSeries = state.stores?.sqTrendSeriesStore?.capsuleSeries;
    const newCapsuleSeries: any[] = [];
    _.forEach(state.stores?.sqTrendStore?.uniqueLanes, (lane) => {
      const yAlignmentValues = performYAlignment(_.filter(capsuleSeries, { lane }));

      _.forEach(yAlignmentValues, (yAlignment) => {
        const series = _.find(capsuleSeries, ['id', yAlignment.id]);
        newCapsuleSeries.push({
          ...series,
          yAlignment: _.omit(yAlignment, 'id', 'autoDisabled'),
          visible: !yAlignment.autoDisabled,
          autoDisabled: yAlignment.autoDisabled,
        });
      });
    });

    _.set(state, 'stores.sqTrendSeriesStore.capsuleSeries', newCapsuleSeries);

    delete state.stores.sqTrendStore.capsuleAlignment;
  }

  return state;
}

/**
 * Converts the similarity column to a regular properties column
 * @param state
 */
function upgrade48(state: any) {
  if (state?.stores?.sqTrendStore?.enabledColumns?.CAPSULES?.similarity) {
    delete state.stores.sqTrendStore.enabledColumns.CAPSULES.similarity;
    _.set(state, 'stores.sqTrendStore.enabledColumns.CAPSULES["properties.Similarity"]', true);
    _.set(state, 'stores.sqTrendStore.propertyColumns.CAPSULES["properties.Similarity"]', {
      key: 'properties.Similarity',
      propertyName: 'Similarity',
      uomKey: 'propertiesUOM.Similarity',
    });
  }

  return state;
}

/**
 * Upgrades condition tables with capsule properties by changing Property columns for condition tables to be
 * CapsuleProperty type.
 * @param state
 */
function upgrade49(state: any) {
  if (_.isArray(state?.stores?.sqTableBuilderStore?.columns?.[TableBuilderMode.Condition])) {
    _.forEach(state.stores.sqTableBuilderStore.columns[TableBuilderMode.Condition], (column, index) => {
      if (column?.type === TableBuilderColumnType.Property) {
        state.stores.sqTableBuilderStore.columns[TableBuilderMode.Condition][index].type =
          TableBuilderColumnType.CapsuleProperty;
      }
    });
  }

  return state;
}

/**
 * Assigns lanes to conditions and shift other item lanes
 * Shifts the lane target for all custom lane labels by the number of condition lanes added
 * @param state
 */
function upgrade50(state: any) {
  const conditionItems = _.sortBy(_.get(state, 'stores.sqTrendCapsuleSetStore.items'), 'conditionLane');

  let conditionsEndLane = 0;
  _.forEach(conditionItems, (item, index) => {
    const newLane = +index + 1;
    item.lane = newLane;
    conditionsEndLane = newLane;
    if (_.has(item, 'conditionLane')) {
      delete item.conditionLane;
    }
  });

  if (conditionsEndLane === 0) {
    return state;
  }

  _.set(state, 'stores.sqTrendCapsuleSetStore.items', conditionItems);

  // Shift other item lanes
  const otherItemStores = [
    'sqTrendSeriesStore',
    'sqTrendScalarStore',
    'sqTrendTableStore',
    'sqTrendMetricStore',
  ] as const;

  const updateOtherStoreItems = (store: (typeof otherItemStores)[number]) => {
    const itemsPath = `stores.${store}.items`;
    const items = _.get(state, itemsPath);

    const itemsWithUpdatedLane = _.chain(items)
      .map((item) => (_.isInteger(item.lane) ? { ...item, lane: item.lane + conditionsEndLane } : item))
      .value();

    _.set(state, itemsPath, itemsWithUpdatedLane);
  };

  _.forEach(otherItemStores, updateOtherStoreItems);

  // Shift lane target for custom lane labels
  const customLabels = _.get(state, 'stores.sqTrendStore.labelDisplayConfiguration.customLabels', []);
  if (!_.isEmpty(customLabels)) {
    const shiftedCustomLabels = customLabels.map((customLabel: any) =>
      customLabel.location === 'lane'
        ? { ...customLabel, target: customLabel.target + conditionsEndLane }
        : customLabel,
    );
    _.set(state, 'stores.sqTrendStore.labelDisplayConfiguration.customLabels', shiftedCustomLabels);
  }

  return state;
}

/**
 * Upgrades chartSettings to accept key for columns and id for rows instead of indexes.
 * @param state
 */
function upgrade51(state: any) {
  if (state?.stores?.sqTableBuilderStore?.chartView?.settings) {
    _.forEach(['columns', 'categoryColumns'], (setting) => {
      if (_.isArray(state.stores.sqTableBuilderStore.chartView.settings[setting])) {
        _.forEach(state.stores.sqTableBuilderStore.chartView.settings[setting], (columnIndex, index) => {
          if (_.isNumber(columnIndex) && state.stores.sqTableBuilderStore.columns.simple[columnIndex]) {
            state.stores.sqTableBuilderStore.chartView.settings[setting].splice(
              index,
              1,
              state.stores.sqTableBuilderStore.columns.simple[columnIndex].key,
            );
          }
        });
      }
    });
    // Old version of rows was indices that pointed to items in the details pane. Since the details pane cannot be
    // reconstructed the setting is emptied.
    if (_.every(state.stores.sqTableBuilderStore.chartView.settings.rows, _.isNumber)) {
      _.set(state, 'stores.sqTableBuilderStore.chartView.settings.rows', []);
    }
  }

  return state;
}

function upgrade52(state: any) {
  const conditions = _.get(state, 'stores.sqTrendCapsuleSetStore.items');
  if (conditions?.length > 0) {
    _.set(
      state,
      'stores.sqTrendCapsuleSetStore.items',
      _.map(conditions, (condition) => ({ ...condition, lineWidth: condition.lineWidth ?? 1 })),
    );
  }

  return state;
}

/**
 * In R44 and R45 there was a bug where if the user opened the modal to add a property column to either the Series
 * or Capsules panel and then clicked cancel it would still add an empty property. While the bug was fixed, an
 * upgrade to remove the invalid state was not written and in R58 that invalid state would cause the worksheet to
 * fail to load (CRAB-36970). This upgrade removes the invalid state.
 *
 * @param state
 */
function upgrade53(state: any) {
  _.unset(state, ['stores', 'sqTrendStore', 'enabledColumns', 'CAPSULES', 'properties.undefined']);
  _.unset(state, ['stores', 'sqTrendStore', 'propertyColumns', 'CAPSULES', 'properties.undefined']);
  _.unset(state, ['stores', 'sqTrendStore', 'enabledColumns', 'SERIES', 'properties.undefined']);
  _.unset(state, ['stores', 'sqTrendStore', 'propertyColumns', 'SERIES', 'properties.undefined']);

  return state;
}

/**
 * Updates the dashboardStore to contain a content property instead of a layout property and adds the now supported
 * properties of title, showTitle and id.
 * @param state
 */
function upgrade54(state: any) {
  const layout = _.get(state, 'stores.sqDashboardStore.layout');
  if (layout) {
    const updatedLayout = layout.map((tile: any) => {
      return { ...tile, type: ContentTypes.Visual, showTitle: true, id: tile.i, title: 'Untitled' };
    });
    _.set(state, 'stores.sqDashboardStore.content', updatedLayout);
    _.unset(state, ['stores', 'sqDashboardStore', 'layout']);
  }

  return state;
}

/**
 * This upgrade addresses 2 changes. One, non-simple metrics should always be inserted into the columns of the
 * tableBuilderStore, in the position that matches where they normally were shown in the table. (After Start/End,
 * but before all other stats/properties). This includes adding their sort/filter fields as well.
 *
 * Two, start/end columns are now movable. Until now, their position in the columns did not matter because they were
 * pulled out and fixed into the table at the position we wanted (Before Metrics or stats and columns). Because they
 * are movable, their index in the array now matters, so they need to be moved to be positioned in front of all
 * other metrics/properties/stats
 *
 * The reordering mechanism must persist the relative positions of all other elements, because some elements in the
 * given array are actually columns while the others are rows and vice versa. So we need to maintain the order of
 * the columns relative to other columns (name, Unit of Measure, and text columns) and the order of rows relative to
 * other rows (stats/metrics/properties)
 * @param state
 */
async function upgrade55(state: any) {
  const trendMetricItems = _.sortBy(_.get(state, 'stores.sqTrendMetricStore.items'), 'lane');
  const allMetricPromises = _.map(trendMetricItems, (metric) => sqMetricsApi.getMetric({ id: metric.id }));

  const allMetricResponses = await Promise.all(allMetricPromises);
  const nonSimpleMetrics: ThresholdMetricOutputV1[] = allMetricResponses
    .map((metricResponse) => metricResponse.data as ThresholdMetricOutputV1)
    .filter(
      (metric) =>
        metric?.processType === ProcessTypeEnum.Condition || metric?.processType === ProcessTypeEnum.Continuous,
    );

  const nonSimpleMetricItems = _.filter(trendMetricItems, (item) =>
    nonSimpleMetrics.some((metric) => metric.id === item.id),
  );

  const selectedNonSimpleMetrics =
    nonSimpleMetricItems.length === 0
      ? nonSimpleMetrics
      : _.filter(nonSimpleMetrics, (metric) => nonSimpleMetricItems.some((item) => item.id === metric.id));

  if (selectedNonSimpleMetrics.length > 0) {
    const conditionTableColumnsAndRows: ColumnOrRow[] = _.get(
      state,
      'stores.sqTableBuilderStore.columns.condition',
      [],
    );
    const tableItemFilters: Record<string, { filter: TableColumnFilter }> = _.get(
      state,
      'stores.sqTableBuilderStore.itemFilters',
      {},
    );

    const tableItemSorts: Record<string, { sort: TableBuilderSort }> = _.get(
      state,
      'stores.sqTableBuilderStore.itemSorts',
      {},
    );
    const metricColumnsToInsert = selectedNonSimpleMetrics
      .filter((metric) => !conditionTableColumnsAndRows.some((row) => row.key === metric.id))
      .map((metric) => {
        const column = { key: metric.id, metricId: metric.id };
        const columnWithSort = tableItemSorts[metric.id] ? { ...column, sort: tableItemSorts[metric.id].sort } : column;
        const columnWithFilter = tableItemFilters[metric.id]
          ? { ...columnWithSort, filter: tableItemFilters[metric.id].filter }
          : columnWithSort;
        return withDefaultFormatting(columnWithFilter);
      });
    if (metricColumnsToInsert.length > 0) {
      // add metrics in front of the first stat or property column
      const indexToAddMetricBefore = _.findIndex(conditionTableColumnsAndRows, (columnOrRow) =>
        isPropertyOrStatColumn(columnOrRow),
      );
      if (indexToAddMetricBefore > -1) {
        conditionTableColumnsAndRows.splice(indexToAddMetricBefore, 0, ...metricColumnsToInsert);
      } else {
        conditionTableColumnsAndRows.push(...metricColumnsToInsert);
      }
      _.set(state, 'stores.sqTableBuilderStore.columns.condition', conditionTableColumnsAndRows);
    }
  }
  const conditionTableColumnsAndRows: ColumnOrRow[] = _.get(state, 'stores.sqTableBuilderStore.columns.condition', []);
  if (conditionTableColumnsAndRows.length > 0) {
    // Move Start/End columns to be before any metric/property/stat column
    const startAndEndColumns = conditionTableColumnsAndRows.filter((columnOrRow) => isStartOrEndColumn(columnOrRow));
    _.remove(conditionTableColumnsAndRows, (columnOrRow) => isStartOrEndColumn(columnOrRow));
    const indexToAddStartAndEndBefore = _.findIndex(conditionTableColumnsAndRows, (columnOrRow) =>
      isPropertyOrStatOrMetricColumn(columnOrRow),
    );
    if (indexToAddStartAndEndBefore > -1) {
      conditionTableColumnsAndRows.splice(indexToAddStartAndEndBefore, 0, ...startAndEndColumns);
    } else {
      conditionTableColumnsAndRows.push(...startAndEndColumns);
    }
    _.set(state, 'stores.sqTableBuilderStore.columns.condition', conditionTableColumnsAndRows);
  }
  return state;
}

/**
 * Fixes broken scatter plot state where the f(x) line was using a y signal that was removed
 * @param state
 */
function upgrade56(state: any) {
  const fxLines = _.get(state, 'stores.sqScatterPlotStore.fxLines');
  const ySignals = _.get(state, 'stores.sqScatterPlotStore.ySignals');
  if (fxLines) {
    const newFxLines = _.filter(fxLines, (fxLine) => _.some(ySignals, { id: fxLine.ySignalId }));
    _.set(state, 'stores.sqScatterPlotStore.fxLines', newFxLines);
  }

  return state;
}

/**
 * Updates the height of the dashboard content to match the new grid (the grid now allows for greater control over
 * the height. Also defaults the new showBorder property to true for all tiles (we only have TextTiles before this.
 * @param state
 */
function upgrade57(state: any) {
  const content = _.get(state, 'stores.sqDashboardStore.content');
  if (content) {
    const updatedHeightContent = content.map((tile: any) => {
      return { ...tile, h: tile.h * 10, showBorder: true };
    });
    _.set(state, 'stores.sqDashboardStore.content', updatedHeightContent);
  }

  return state;
}

/**
 * Updates the width of the dashboard content to match the new grid (the grid now allows for 10 columns)
 * @param state
 */
function upgrade58(state: any) {
  const content = _.get(state, 'stores.sqDashboardStore.content');
  if (content) {
    const updatedWidthContent = content.map((tile: any) => {
      return { ...tile, w: tile.w * 3 };
    });
    _.set(state, 'stores.sqDashboardStore.content', updatedWidthContent);
  }

  return state;
}

/**
 * Flattens condition table filter and sort properties for metric columns.
 * This fixes state that upgrade55 transformed into a broken format (CRAB-41147)
 *
 * @param state
 */
function upgrade59(state: any) {
  const conditionTableColumns: any[] = _.get(state, 'stores.sqTableBuilderStore.columns.condition');
  if (conditionTableColumns) {
    conditionTableColumns
      .filter((column) => column.filter?.filter)
      .forEach((column) => {
        column.filter = column.filter.filter;
      });

    conditionTableColumns
      .filter((column) => column.sort?.sort)
      .forEach((column) => {
        column.sort = column.sort.sort;
      });

    _.set(state, 'stores.sqTableBuilderStore.columns.condition', conditionTableColumns);
  }

  return state;
}

/** Ensure that Asset Group Editor and Table Editor views are added as tabs **/
function upgrade60(state: any) {
  const currentView = _.get(state, 'stores.sqWorksheetStore.viewKey');
  if (currentView === ASSET_GROUP_VIEW.key) {
    _.set(state, 'stores.sqWorksheetStore.displayAssetGroupEditor', true);
    _.set(state, 'stores.sqWorksheetStore.viewKey', WORKSHEET_VIEW.TREND);
  } else if (currentView === TABLE_DEFINITION_VIEW.key) {
    _.set(state, 'stores.sqWorksheetStore.displayTableDefinitionView', true);
    _.set(state, 'stores.sqWorksheetStore.viewKey', WORKSHEET_VIEW.TREND);
  }
  return state;
}

/** Update the store key values from `sqTrendCapsuleSetStore` to `sqTrendConditionStore` **/
function upgrade61(state: any) {
  const sqTrendCapsuleSetStore = _.get(state, 'stores.sqTrendCapsuleSetStore');

  if (sqTrendCapsuleSetStore) {
    _.set(state, 'stores.sqTrendConditionStore', sqTrendCapsuleSetStore);
    _.unset(state, 'stores.sqTrendCapsuleSetStore');
  }

  return state;
}

/** Migrate legacy Vantage worksteps to the flux store **/
function upgrade63(state: any, fullWorkstep?: any) {
  const columnStateToMigrate = _.get(fullWorkstep, 'content.colState');
  if (columnStateToMigrate === undefined) {
    return state;
  }

  const groupColIds = _.chain(columnStateToMigrate)
    .filter((column) => column.rowGroup)
    .orderBy(['rowGroupIndex'], ['asc'])
    .map((column: any) => column.colId)
    .value();

  const sqVantageTableStore = {
    gridState: {
      column: _.get(fullWorkstep, 'content.colState'),
      filter: _.get(fullWorkstep, 'content.filterState'),
      row: {
        group: {
          groupColIds,
        },
        selection: [],
      },
    },
    quickFilter: _.get(fullWorkstep, 'content.fullTableFilter'),
    dateRange: _.get(fullWorkstep, 'content.dateRange'),
  };

  _.set(state, 'stores.sqVantageTableStore', sqVantageTableStore);
  _.unset(state, 'content');
  return state;
}

/** Update the store key values from `sqValueCaptureStore` to `sqImpactReportStore` **/
function upgrade64(state: any) {
  const sqValueCaptureStore = _.get(state, 'stores.sqValueCaptureStore');

  if (sqValueCaptureStore) {
    _.set(state, 'stores.sqImpactReportStore', sqValueCaptureStore);
    _.unset(state, 'stores.sqValueCaptureStore');
  }

  return state;
}

/** Migrate sqVantageTableStore dateRange data to the duration store **/
function upgrade65(state: any) {
  const legacyDateRange = _.get(state, 'stores.sqVantageTableStore.dateRange');
  if (!legacyDateRange || !sqWorkbookStore.isVantage) {
    return state;
  }

  const lookBack = moment
    .duration(legacyDateRange.lookBack?.num ?? 7, legacyDateRange.lookBack?.unit?.value ?? 'days')
    .asMilliseconds();
  const lookForward = moment
    .duration(legacyDateRange.lookForward?.num ?? 1, legacyDateRange.lookForward?.unit?.value ?? 'days')
    .asMilliseconds();

  if (lookBack > 0 || lookForward > 0) {
    _.set(state, 'stores.sqDurationStore.autoUpdate.mode', 'MANUAL');
    _.set(state, 'stores.sqDurationStore.autoUpdate.offset', lookForward);
    _.set(state, 'stores.sqDurationStore.displayRange.start', sqDurationStore.autoUpdate.now - lookBack);
    _.set(state, 'stores.sqDurationStore.displayRange.end', sqDurationStore.autoUpdate.now + lookForward);
  } else {
    _.set(state, 'stores.sqDurationStore.autoUpdate.mode', 'OFF');
    _.set(state, 'stores.sqDurationStore.displayRange.start', moment.utc(legacyDateRange.range[0]).valueOf());
    _.set(state, 'stores.sqDurationStore.displayRange.end', moment.utc(legacyDateRange.range[1]).valueOf());
  }

  _.unset(state, 'stores.sqVantageTableStore.dateRange');
  return state;
}

/**
 * Replaces all usages of capsuleSet* with condition*
 */
function upgrade66(state: any) {
  const sqCapsuleGroupStore = _.get(state, 'stores.sqCapsuleGroupStore');
  if (!sqCapsuleGroupStore?.capsules?.length) {
    return state;
  }

  // Rename `capsuleSetId` to `conditionId`
  sqCapsuleGroupStore.capsules = sqCapsuleGroupStore.capsules.map((capsule: any) => {
    _.set(capsule, 'propertiesSource.conditionId', capsule.propertiesSource?.capsuleSetId);
    _.unset(capsule, 'propertiesSource.capsuleSetId');

    return capsule;
  });

  return state;
}

/** Migrate sqTableBuilderStore Highcharts Simple Tables to AgGrid Charts **/
function upgrade67(state: any) {
  const chartViewEnabled = _.get(state, 'stores.sqTableBuilderStore.chartView.enabled');
  const cellRange = _.get(state, 'stores.sqTableBuilderStore.chartView.settings.cellRange');

  if (!chartViewEnabled || !!cellRange) {
    return state;
  }

  const chartSettings = _.get(state, 'stores.sqTableBuilderStore.chartView.settings');
  const categoryColumns: string[] = _.get(state, 'stores.sqTableBuilderStore.chartView.settings.categoryColumns', []);
  const columns: string[] = _.get(state, 'stores.sqTableBuilderStore.chartView.settings.columns', []);
  const currentTransposed: boolean = _.get(state, 'stores.sqTableBuilderStore.isTransposed.simple');
  const oldChartType: string = _.get(state, 'stores.sqTableBuilderStore.chartView.settings.chartType');
  const currentColumns: ColumnOrRowWithDefinitions[] = _.get(state, 'stores.sqTableBuilderStore.columns.simple', []);

  let newChartType;
  if (oldChartType === 'column_stacked') {
    newChartType = 'stackedColumn';
  } else if (oldChartType === 'bar_stacked') {
    newChartType = 'stackedBar';
  } else {
    newChartType = oldChartType;
  }

  _.set(
    state,
    'stores.sqTableBuilderStore.isTransposed.simple',
    oldChartType !== 'pie' ? !currentTransposed : currentTransposed,
  );

  _.set(state, 'stores.sqTableBuilderStore.chartView.settings', {
    ...chartSettings,
    chartType: newChartType,
  });

  const defaultRange = columns.length ? [...categoryColumns, ...columns] : [];
  if (currentTransposed && oldChartType !== 'pie') {
    _.set(state, 'stores.sqTableBuilderStore.chartView.settings.cellRange', {
      columns: defaultRange.length ? defaultRange : currentColumns.map((col) => col.key),
    });
  } else {
    const newRange = defaultRange.length
      ? currentColumns.filter((col) => defaultRange.includes(col.key))
      : currentColumns;

    _.set(state, 'stores.sqTableBuilderStore.columns.simple', newRange);
    _.set(state, 'stores.sqTableBuilderStore.chartView.settings.cellRange', {
      columns: newRange.map((col) => col.key),
    });
  }

  _.set(state, 'stores.sqTableBuilderStore.chartView.settings.chartOptions', {
    common: {
      axes: {
        category: {
          label: { enabled: chartSettings?.categoryLabels },
        },
      },
      legend: {
        enabled: chartSettings?.legend,
      },
      series: {
        label: {
          enabled: chartSettings?.dataLabels,
        },
      },
    },
  });

  const keysToRemove = [
    'categoryColumns',
    'categoryLabels',
    'columns',
    'dataLabels',
    'legend',
    'position',
    'rows',
    'showSettings',
    'showHeader',
    'title',
  ];

  keysToRemove.forEach((key) => {
    _.unset(state, `stores.sqTableBuilderStore.chartView.settings.${key}`);
  });

  return state;
}

function upgrade68(state: any) {
  if (state?.stores?.sqVantageTableStore?.gridState?.column) {
    const aggFuncMappings = { First: 'first', Last: 'last', Sum: 'sum' };
    state.stores.sqVantageTableStore.gridState.column.forEach((column: { aggFunc: string }) => {
      const newMapping = (aggFuncMappings as AnyProperty<string>)[column.aggFunc];
      if (newMapping) {
        column.aggFunc = newMapping;
      }
    });
  }
  return state;
}

/**
 * Updates the scaling table store to have a new tableId property instead of scalingTableId
 */
function upgrade69(state: any) {
  const scalingTableStore = _.get(state, 'stores.sqScalingTableStore');
  if (scalingTableStore) {
    _.set(state, 'stores.sqScalingTableStore.tableId', scalingTableStore.scalingTableId);
  }
  return state;
}

/** CRAB-48609: Give saved cursors a default yOffset **/
function upgrade70(state: any) {
  const DEFAULT_Y_OFFSET = 90;
  const capsuleCursors = _.get(state, 'stores.sqCursorStore.capsuleCursors');
  const calendarCursors = _.get(state, 'stores.sqCursorStore.calendarCursors');
  [capsuleCursors, calendarCursors].forEach((cursors) => {
    if (cursors) {
      cursors.forEach((cursor: Cursor) => {
        if (cursor.yOffset === undefined) {
          _.set(cursor, 'yOffset', DEFAULT_Y_OFFSET);
        }
      });
    }
  });
  return state;
}

// CRAB-43345: Need an associated interactive content upgrade to add a new property
const upgrade62 = _.identity;

const upgrade41 = _.identity; // CRAB-27022 added an upgrade to 53, so this is necessary for forward compatibility
const upgrade40 = _.identity; // CRAB-27022 added an upgrade to 53, so this is necessary for forward compatibility
// CRAB-24258: A backport added upgrade26 to R50.3.0 which prevented a different upgrade26 function in R51.0.0
// and greater from executing. We fix this by ensuring the logic for upgrades 26 - 31 is idempotent and moving
// those upgrades to later workstep schema versions to ensure they all execute.
const upgrade26 = _.identity;
const upgrade27 = _.identity;
const upgrade28 = _.identity;
const upgrade29 = _.identity;
const upgrade30 = _.identity;
const upgrade31 = _.identity;
const upgrade71 = _.identity; // Only affects interactive content

function upgrade72(state: any) {
  if (!sqWorkbookStore.isVantage) {
    return state;
  }

  const lookback = _.get(state, 'stores.sqVantageTrendStore.lookback');

  if (lookback === undefined) {
    return state;
  }

  const oldDisplayRange = _.get(state, 'stores.sqDurationStore.displayRange');

  _.set(state, 'stores.sqDurationStore.investigateRange', {
    start: oldDisplayRange.start,
    end: oldDisplayRange.end,
  });

  _.set(state, 'stores.sqDurationStore.displayRange', {
    start: oldDisplayRange.start - lookback,
    end: oldDisplayRange.end,
  });

  _.unset(state, 'stores.sqVantageTrendStore.lookback');

  return state;
}

/**
 * CRAB-50058: Updates the search store to rename table-definitions search mode to scaling-tables
 */
function upgrade73(state: any) {
  const searchStore = _.get(state, 'stores.sqSearchStore');
  if (searchStore) {
    const oldTableDefinitionsMode = _.get(state, 'stores.sqSearchStore.mode');
    if (oldTableDefinitionsMode === 'table-definitions') {
      _.set(state, 'stores.sqSearchStore.mode', 'scaling-tables');
    }
  }
  return state;
}

/**
 * CRAB-48985: Saving the chart excluded series and filtering them out of all available columns
 */
function upgrade74(state: any) {
  // Simple table
  const simpleTableSettings = _.get(state, 'stores.sqTableBuilderStore.chartView.settings');
  if (simpleTableSettings) {
    const isSimpleTransposed = _.get(state, 'stores.sqTableBuilderStore.isTransposed.simple', false);
    const simpleTrendCondition = _.get(state, 'stores.sqTrendConditionStore.items', []).map(
      (item: ConditionItem) => item.id,
    );
    const simpleTrendSeries = _.get(state, 'stores.sqTrendSeriesStore.items', []).map((item: SeriesItem) => item.id);
    const simpleTableSignals = _.concat(simpleTrendCondition, simpleTrendSeries);

    const simpleTableColumns: ColumnOrRowWithDefinitions[] = _.get(
      state,
      'stores.sqTableBuilderStore.columns.simple',
      [],
    ).map((col: ColumnOrRowWithDefinitions) => col.key.replace('.', '_'));

    const allSimpleAvailableColumnIds = [
      ...(isSimpleTransposed ? [SIMPLE_TABLE_TRANSPOSED_HEADER_COLUMN, ...simpleTableSignals] : simpleTableColumns),
    ];

    const simpleTableChartColumns = _.get(state, 'stores.sqTableBuilderStore.chartView.settings.cellRange.columns', []);
    const excludedSimpleIds = !simpleTableChartColumns.length
      ? []
      : allSimpleAvailableColumnIds.filter(
          (colId): colId is string => typeof colId === 'string' && !simpleTableChartColumns.includes(colId),
        );

    _.set(state, 'stores.sqTableBuilderStore.chartView.settings.excludedColumnIds', excludedSimpleIds);
  }

  // Condition table
  const conditionTableSettings = _.get(state, 'stores.sqTableBuilderStore.chartView.conditionSettings');
  if (conditionTableSettings) {
    const isConditionTransposed = _.get(state, 'stores.sqTableBuilderStore.isTransposed.condition', false);

    const conditionTableColumns: ColumnOrRowWithDefinitions[] = _.get(
      state,
      'stores.sqTableBuilderStore.columns.condition',
      [],
    ).map((col: ColumnOrRowWithDefinitions) => col.key);

    const allConditionAvailableColumnIds = isConditionTransposed
      ? [CONDITION_TABLE_TRANSPOSED_HEADER_COLUMN, ...conditionTableColumns]
      : [];

    const conditionTableChartColumns = _.get(
      state,
      'stores.sqTableBuilderStore.chartView.conditionSettings.cellRange.columns',
      [],
    );
    const excludedConditionIds = !conditionTableChartColumns?.length
      ? []
      : allConditionAvailableColumnIds.filter(
          (colId): colId is string => typeof colId === 'string' && !conditionTableChartColumns.includes(colId),
        );

    _.set(state, 'stores.sqTableBuilderStore.chartView.conditionSettings.excludedColumnIds', excludedConditionIds);
  }

  return state;
}

/**
 * CRAB-49500: Modify any Details pane items using old To Simple Table Scaling names to use new format.
 * Removes all existing items in the sqTrendScalingColumnStore.
 */
async function upgrade75(state: any) {
  const trendScalingColumnStore = _.get(state, 'stores.sqTrendScalingColumnStore');
  if (trendScalingColumnStore) {
    _.set(state, 'stores.sqTrendScalingColumnStore.items', []);
  }

  return state;
}

/**
 * CRAB-51280: Remove the includeHierarchy attribute from the sqItemfinderStore.
 */
function upgrade76(state: any[]) {
  const itemFinderStore = _.get(state, 'stores.sqItemFinderStore');
  if (itemFinderStore) {
    _.forEach(itemFinderStore.items, (item) => {
      _.unset(item, 'includeHierarchy');
    });
  }

  return state;
}

/**
 * CRAB-51714: update the isFormulaAcross to differentiate between mode
 */
function upgrade77(state: any[]) {
  const tableBuilderStore = _.get(state, 'stores.sqTableBuilderStore');
  if (tableBuilderStore) {
    _.set(tableBuilderStore, 'isFormulaAcrossTable', {
      simple: tableBuilderStore.isFormulaAcrossTable,
      condition: false,
    });
  }

  return state;
}

/** CRAB-50396: Filtering out statistic condition columns without an item */
function upgrade78(state: any) {
  const trendSeries = _.get(state, 'stores.sqTrendSeriesStore.items', []);
  const conditionColumns: ColumnOrRowWithDefinitions[] = _.get(
    state,
    'stores.sqTableBuilderStore.columns.condition',
    [],
  );

  if (!conditionColumns.length) {
    return state;
  }

  const filteredConditionColumns = conditionColumns.filter((col) => {
    if (col.key && isStatColumn(col)) {
      const potentialItemId = col.signalId;
      const potentialItem = trendSeries.find((item: SeriesItem) => item.id === potentialItemId);
      return !!potentialItem;
    }
    return true;
  });

  _.set(state, 'stores.sqTableBuilderStore.columns.condition', filteredConditionColumns);

  return state;
}

/** Add column id for scaling table items in details pane **/
async function upgrade79(state: any) {
  const stores: string[] = ['sqTrendSeriesStore', 'sqTrendScalarStore', 'sqTrendConditionStore'];
  for (const store of stores) {
    const items = _.get(state, `stores.${store}.items`, []);
    if (items.length) {
      const updatedItems = await Promise.all(
        items.map(async (item: any) => {
          if (item.rowId && item.tableId && !item.columnId) {
            const { materializedTableRow, scalingTableDefinition } = await getItemsInScalingTableRow(
              item.tableId,
              item.rowId,
            );
            const columnName = materializedTableRow.find((column) => column.uuid === item.id)?.columnName;
            const column = scalingTableDefinition.columnDefinitions.find((col) => col.columnName === columnName);
            const columnId = column?.id;
            return { ...item, columnId };
          }
          return item;
        }),
      );
      _.set(state, `stores.${store}.items`, updatedItems);
    }
  }
  return state;
}

/**
 * CRAB-50622: Upgrades Vantage table filters to use the new multimodal filter format.
 * Converts old filter format { filterType: 'set', values: [...] } to the new format
 * { filterMode: 'Default', selectedValues: [...], filterType: 'set', values: [...] }
 * for the Suppressed, Flagged, and Labels columns.
 */
function upgrade80(state: any) {
  if (!sqWorkbookStore.isVantage) {
    return state;
  }

  const filterState = _.get(state, 'stores.sqVantageTableStore.gridState.filter');
  if (!filterState) {
    return state;
  }

  const columnsToUpgrade = [VantageNames.Columns.Suppressed, VantageNames.Columns.Flagged, VantageNames.Columns.Labels];

  columnsToUpgrade.forEach((columnName) => {
    const columnFilter = filterState[columnName];
    if (!columnFilter) {
      return;
    }

    // Check if this is an old format filter (has filterType and values, but no filterMode)
    const hasOldFormat =
      columnFilter.filterType === 'set' && Array.isArray(columnFilter.values) && !columnFilter.filterMode;

    if (hasOldFormat) {
      // Upgrade to new format with Default mode
      filterState[columnName] = {
        filterMode: 'Default',
        selectedValues: columnFilter.values,
        filterType: 'set',
        values: columnFilter.values,
      };
    }
  });

  _.set(state, 'stores.sqVantageTableStore.gridState.filter', filterState);
  return state;
}

function upgrade81(state: any) {
  const vantageTrendStore = _.get(state, 'stores.sqVantageTrendStore');
  if (vantageTrendStore && _.isUndefined(vantageTrendStore.ganttShowAll)) {
    _.set(state, 'stores.sqVantageTrendStore.ganttShowAll', false);
  }
  return state;
}

/**
 * CRAB-50864: Move legacy layout preferences from sqLayoutStore into localStorage and remove them from workstep state.
 */
function upgrade82(state: any) {
  const layoutStore = _.get(state, 'stores.sqLayoutStore');
  if (!layoutStore) {
    return state;
  }

  migrateLegacyLayoutPreferences({
    sidePanelCollapsed: layoutStore.sidePanelCollapsed,
    toolsTabVisible: layoutStore.toolsTabVisible,
    calculationHierarchyInTab: layoutStore.calculationHierarchyInTab,
    formulaToolSize: layoutStore.formulaToolSize,
  });

  delete layoutStore.sidePanelCollapsed;
  delete layoutStore.toolsTabVisible;
  delete layoutStore.calculationHierarchyInTab;
  delete layoutStore.formulaToolSize;

  return state;
}

/**
 * CRAB-54110: Migrate sqVantageTableStore.gridState from the legacy custom format
 * (column/filter/row) to AG Grid's native GridState format (columnOrder/columnVisibility/
 * columnSizing/filter.filterModel/rowGroup/rowGroupExpansion/rowSelection).
 */
function upgrade83(state: any) {
  const gridState = _.get(state, 'stores.sqVantageTableStore.gridState');
  if (!gridState) {
    return state;
  }

  // Already in native format — nothing to migrate
  if (gridState.columnOrder || gridState.rowGroup || gridState.columnVisibility) {
    return state;
  }

  // column[] → native column state (columnOrder, columnVisibility, columnSizing, sort, rowGroup, etc.)
  // Uses AG Grid's built-in convertColumnState utility for accurate conversion.
  const columnState = gridState.column && Array.isArray(gridState.column) ? convertColumnState(gridState.column) : {};

  const native: any = { ...columnState };

  // flat FilterModel → nested { filterModel: FilterModel }
  if (gridState.filter) {
    native.filter = { filterModel: gridState.filter };
  }

  // row.group → rowGroup
  if (Array.isArray(gridState.row?.group?.groupColIds)) {
    native.rowGroup = { groupColIds: gridState.row.group.groupColIds };
  }

  // row.groupExpansion → rowGroupExpansion
  if (gridState.row?.groupExpansion) {
    const ge = gridState.row.groupExpansion;
    native.rowGroupExpansion = {
      expandedRowGroupIds: Array.isArray(ge.expandedRowGroupIds) ? ge.expandedRowGroupIds : [],
    };
  }

  // row.selection → rowSelection
  // Preserve stored IDs as-is. The leaf row ID suffix differs between CSRM (always "-0",
  // captured before grouping) and SSRM ("-N" where N = number of group columns). Instead
  // of rewriting the suffix in the migration (which would break whichever row model isn't
  // the current target), the load-time logic in `useEvidenceTable` and `applySerializedGridState`
  // is responsible for normalizing IDs to the active row model's format.
  if (gridState.row?.selection) {
    native.rowSelection = gridState.row.selection;
  }

  _.set(state, 'stores.sqVantageTableStore.gridState', native);
  return state;
}

/** Update the store key values from `sqToolModelStore` to `sqToolTemplateStore`
 *  and from `sqScalingModelStore` to `sqScalingTemplateStore` **/
function upgrade84(state: any) {
  const sqToolModelStore = _.get(state, 'stores.sqToolModelStore');
  if (sqToolModelStore) {
    _.set(state, 'stores.sqToolTemplateStore', sqToolModelStore);
    _.unset(state, 'stores.sqToolModelStore');
  }

  const sqScalingModelStore = _.get(state, 'stores.sqScalingModelStore');
  if (sqScalingModelStore) {
    _.set(state, 'stores.sqScalingTemplateStore', sqScalingModelStore);
    _.unset(state, 'stores.sqScalingModelStore');
  }

  const sqModelLibraryStore = _.get(state, 'stores.sqModelLibraryStore');
  if (sqModelLibraryStore) {
    _.set(state, 'stores.sqTemplateLibraryStore', sqModelLibraryStore);
    _.unset(state, 'stores.sqModelLibraryStore');
  }

  return state;
}

/**
 * CRAB-60111: Rename TABLE_DEFINITION_COLUMN_OPTIONS values from *_MODEL to *_TEMPLATE
 * in sqScalingTableEditorStore and sqItemTableEditorStore selectedColumnOption.
 */
function upgrade85(state: any) {
  const MODEL_TO_TEMPLATE: Record<string, string> = {
    EDIT_MODEL: 'EDIT_TEMPLATE',
    CREATE_MODEL: 'CREATE_TEMPLATE',
    APPLY_MODEL: 'APPLY_TEMPLATE',
  };

  const storeNames = ['sqScalingTableEditorStore', 'sqItemTableEditorStore'];
  for (const storeName of storeNames) {
    const value = _.get(state, `stores.${storeName}.selectedColumnOption.value`);
    if (value && MODEL_TO_TEMPLATE[value]) {
      _.set(state, `stores.${storeName}.selectedColumnOption.value`, MODEL_TO_TEMPLATE[value]);
    }
  }

  return state;
}

/**
 * CRAB-60976: Move selectedColumnOption values in sqScalingTableEditorStore to selectedTemplateOption if they are
 * template actions and update the visible pane.
 *
 * Prior to this change, template actions (APPLY_TEMPLATE, CREATE_TEMPLATE, EDIT_TEMPLATE) were stored as
 * selectedColumnOption values (TableDefinitionColumnOptions type). They now belong on selectedTemplateOption
 * (TableDefinitionTemplateOptions type) and the visible pane should be 'template' not 'column'. If both
 * selectedTemplateOption and selectedColumnOption are set to a template action, then it is fine/necessary to unset
 * the column action as template actions no longer exist in the column options.
 */
function upgrade86(state: any) {
  const COLUMN_VALUE_TO_TEMPLATE_OPTION: Record<string, TableDefinitionTemplateOptions> = {
    [TABLE_DEFINITION_TEMPLATE_OPTIONS.APPLY_TEMPLATE.value]: TABLE_DEFINITION_TEMPLATE_OPTIONS.APPLY_TEMPLATE,
    [TABLE_DEFINITION_TEMPLATE_OPTIONS.CREATE_TEMPLATE.value]: TABLE_DEFINITION_TEMPLATE_OPTIONS.CREATE_TEMPLATE,
    [TABLE_DEFINITION_TEMPLATE_OPTIONS.EDIT_TEMPLATE.value]: TABLE_DEFINITION_TEMPLATE_OPTIONS.EDIT_TEMPLATE,
  };
  const storeNames = ['sqScalingTableEditorStore', 'sqItemTableEditorStore'];
  for (const storeName of storeNames) {
    const selectedColumnOption = _.get(state, `stores.${storeName}.selectedColumnOption`);
    const templateOption = selectedColumnOption && COLUMN_VALUE_TO_TEMPLATE_OPTION[selectedColumnOption.value];
    if (templateOption) {
      _.set(state, `stores.${storeName}.selectedTemplateOption`, templateOption);
      _.unset(state, `stores.${storeName}.selectedColumnOption`);
      _.set(state, `stores.${storeName}.visibleControlPane`, 'template');
    }
  }
  return state;
}

/**
 * CRAB-60745: Migrates sqSummaryReportStore.visibleStats entries from 'average' to 'sampleMean' to match the
 * SummarizeSignal/StatsSummary property rename.
 */
function upgrade87(state: any) {
  const visibleStats = _.get(state, 'stores.sqSummaryReportStore.visibleStats') as string[] | undefined;
  if (_.isArray(visibleStats) && _.includes(visibleStats, 'average')) {
    _.set(
      state,
      'stores.sqSummaryReportStore.visibleStats',
      visibleStats.map((stat) => (stat === 'average' ? 'sampleMean' : stat)),
    );
  }
  return state;
}

const upgraders: Record<string, (state: any, fullWorkstep?: any) => any> = {
  upgrade1,
  upgrade2,
  upgrade3,
  upgrade4,
  upgrade5,
  upgrade6,
  upgrade7,
  upgrade8,
  upgrade9,
  upgrade10,
  upgrade11,
  upgrade12,
  upgrade13,
  upgrade14,
  upgrade15,
  upgrade16,
  upgrade17,
  upgrade18,
  upgrade19,
  upgrade20,
  upgrade21,
  upgrade22,
  upgrade23,
  upgrade24,
  upgrade25,
  upgrade26,
  upgrade27,
  upgrade28,
  upgrade29,
  upgrade30,
  upgrade31,
  upgrade32,
  upgrade33,
  upgrade34,
  upgrade35,
  upgrade36,
  upgrade37,
  upgrade38,
  upgrade39,
  upgrade40,
  upgrade41,
  upgrade42,
  upgrade43,
  upgrade44,
  upgrade45,
  upgrade46,
  upgrade47,
  upgrade48,
  upgrade49,
  upgrade50,
  upgrade51,
  upgrade52,
  upgrade53,
  upgrade54,
  upgrade55,
  upgrade56,
  upgrade57,
  upgrade58,
  upgrade59,
  upgrade60,
  upgrade61,
  upgrade62,
  upgrade63,
  upgrade64,
  upgrade65,
  upgrade66,
  upgrade67,
  upgrade68,
  upgrade69,
  upgrade70,
  upgrade71,
  upgrade72,
  upgrade73,
  upgrade74,
  upgrade75,
  upgrade76,
  upgrade77,
  upgrade78,
  upgrade79,
  upgrade80,
  upgrade81,
  upgrade82,
  upgrade83,
  upgrade84,
  upgrade85,
  upgrade86,
  upgrade87,
};
